import {
  extractOrder,
  productLabelOf,
  shelfPriceMinor,
  statedQuantity,
  type CatalogueItem,
} from './order-extract.ts';
import { normalizeForMatch } from './routing.ts';

/**
 * The AI assistant's built-in rules: what to say and do for one customer message. Pure and deterministic, in
 * English and Gulf Arabic. It answers price, stock, delivery, payment, hours and store questions; takes orders
 * (always asking the customer to confirm first); offers the waitlist for sold-out products; and hands over to a
 * person when asked, on a complaint, when it does not understand, or after too many turns.
 *
 * A configured language model (assistant-model.ts) handles free conversation with the same tools and guardrails;
 * these rules are what runs without one, and the fallback when the model fails a guardrail.
 */
export type Lang = 'en' | 'ar';

export interface BasketLine {
  productId: string;
  label: string;
  quantity: number;
  unitPriceMinor: number;
  /** The usual price when it is on sale (said once, when the order is proposed). */
  regularPriceMinor?: number;
}

export interface AssistantState {
  basket?: BasketLine[];
  /** What the last reply asked the customer. */
  awaiting?: 'confirm' | 'order_offer' | 'waitlist' | null;
  waitlistProductId?: string;
  turns?: number;
  /** Messages in a row the assistant did not understand. */
  misses?: number;
}

export interface KnowledgeItem {
  id: string;
  topic: 'delivery' | 'payment' | 'hours' | 'returns' | 'location' | 'general';
  question: string;
  answer: string;
  answerAr: string | null;
  keywords: string[];
}

export type HandoffReason =
  'asked_for_person' | 'complaint' | 'not_understood' | 'too_many_turns' | 'orders_by_staff';

export type AssistantAction =
  | { type: 'record_order'; lines: { productId: string; quantity: number }[] }
  | { type: 'waitlist'; productId: string }
  | { type: 'handoff'; reason: HandoffReason };

export interface TurnInput {
  text: string;
  lang: Lang;
  customerName: string | null;
  state: AssistantState;
  catalogue: CatalogueItem[];
  knowledge: KnowledgeItem[];
  takeOrders: boolean;
  maxTurns: number;
  /** Formats minor units in the tenant currency for the customer's language. */
  money: (minor: number, lang: Lang) => string;
  /** Voice calls: no "reply YES", nothing to click. */
  spoken?: boolean;
}

export interface TurnResult {
  /** `{{order}}` is replaced with the order number once the order is recorded. */
  reply: string;
  actions: AssistantAction[];
  state: AssistantState;
  intent: string;
  /** Knowledge items the answer came from. */
  knowledgeIds: string[];
}

// ---------------------------------------------------------------------------------------------------- intents

const words = (ws: string[]) => ws.map(normalizeForMatch);
const INTENTS = {
  human: words([
    'human',
    'real person',
    'a person',
    'agent',
    'representative',
    'customer service',
    'someone',
    'staff',
    'speak to',
    'talk to',
    'call me',
    'موظف',
    'شخص',
    'انسان',
    'خدمة العملاء',
    'ابي اكلم',
    'ابغى اكلم',
    'ابغي اكلم',
    'كلمني',
    'اتصلوا',
    'احد يكلمني',
  ]),
  complaint: words([
    'complain',
    'complaint',
    'angry',
    'terrible',
    'worst',
    'broken',
    'damaged',
    'fake',
    'scam',
    'never arrived',
    'not arrived',
    'wrong item',
    'wrong order',
    'disappointed',
    'shame',
    'شكوى',
    'زعلان',
    'سيء',
    'خربان',
    'مكسور',
    'مزيف',
    'ما وصل',
    'ماوصل',
    'غلط',
    'نصب',
  ]),
  yes: words([
    'yes',
    'yeah',
    'yep',
    'yup',
    'sure',
    'ok',
    'okay',
    'confirm',
    'confirmed',
    'go ahead',
    'please do',
    'correct',
    'deal',
    'y',
    'نعم',
    'ايه',
    'ايوه',
    'اي',
    'تمام',
    'اكيد',
    'موافق',
    'اوكي',
    'اكد',
    'يب',
    'زين',
    'طيب',
  ]),
  no: words([
    'no',
    'nope',
    'cancel',
    'not now',
    'later',
    'لا',
    'لاء',
    'الغ',
    'الغي',
    'الغاء',
    'مو الحين',
    'بعدين',
    'خلاص لا',
  ]),
  greeting: words([
    'hi',
    'hello',
    'hey',
    'good morning',
    'good evening',
    'good afternoon',
    'salam',
    'assalamu alaikum',
    'السلام عليكم',
    'مرحبا',
    'هلا',
    'اهلا',
    'صباح الخير',
    'مساء الخير',
  ]),
  thanks: words([
    'thanks',
    'thank you',
    'thx',
    'shukran',
    'شكرا',
    'مشكور',
    'يعطيك العافيه',
    'تسلم',
    'الله يعطيك العافيه',
  ]),
};
const TOPIC_WORDS: Record<KnowledgeItem['topic'], string[]> = {
  delivery: words([
    'deliver',
    'delivery',
    'shipping',
    'ship',
    'courier',
    'arrive',
    'توصيل',
    'شحن',
    'يوصل',
    'توصلون',
    'التوصيل',
  ]),
  payment: words([
    'pay',
    'payment',
    'cash',
    'card',
    'apple pay',
    'cod',
    'installment',
    'دفع',
    'كاش',
    'بطاقه',
    'فيزا',
    'تقسيط',
  ]),
  hours: words([
    'open',
    'opening',
    'hours',
    'close',
    'closing',
    'timing',
    'دوام',
    'تفتح',
    'تسكر',
    'تقفل',
    'اوقات',
    'متى تفتحون',
  ]),
  location: words([
    'where',
    'location',
    'branch',
    'branches',
    'store',
    'stores',
    'mall',
    'address',
    'وين',
    'موقع',
    'فرع',
    'فروع',
    'عنوان',
    'مول',
  ]),
  returns: words(['return', 'exchange', 'refund', 'ارجاع', 'استرجاع', 'استبدال', 'ترجيع']),
  general: [],
};

const clean = (s: string) =>
  normalizeForMatch(s)
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Phrase match on whole words; Arabic words may carry a prefix (و، ب، ال). */
function has(text: string, list: string[]): boolean {
  const t = ` ${clean(text)} `;
  const tokens = t.trim().split(' ');
  return list.some((w) =>
    w.includes(' ')
      ? t.includes(` ${w} `)
      : tokens.some(
          (x) =>
            x === w ||
            (/[؀-ۿ]/.test(w) &&
              x.length > w.length &&
              x.endsWith(w) &&
              /^(?:[وفبلك])?(?:ال)?$/u.test(x.slice(0, -w.length))),
        ),
  );
}

export function classify(text: string) {
  const short = clean(text).split(' ').length <= 4;
  return {
    human: has(text, INTENTS.human),
    complaint: has(text, INTENTS.complaint),
    // "yes" / "no" only count in a short answer ("yes please", "لا شكرا"), not inside a longer sentence.
    yes: short && has(text, INTENTS.yes) && !has(text, INTENTS.no),
    no: short && has(text, INTENTS.no),
    greeting: has(text, INTENTS.greeting),
    thanks: has(text, INTENTS.thanks),
    topic:
      (Object.keys(TOPIC_WORDS) as KnowledgeItem['topic'][]).find((k) => has(text, TOPIC_WORDS[k])) ?? null,
  };
}

/** The knowledge item that answers this message, if any (topic words, the tenant's keywords, the question). */
export function matchKnowledge(text: string, items: KnowledgeItem[]): KnowledgeItem | null {
  const c = classify(text);
  const t = clean(text).split(' ');
  let best: { k: KnowledgeItem; s: number } | null = null;
  for (const k of items) {
    let s = 0;
    if (c.topic && k.topic === c.topic) s += 3;
    if (k.keywords.length && has(text, words(k.keywords))) s += 3;
    const q = clean(k.question)
      .split(' ')
      .filter((w) => w.length > 3);
    s += q.filter((w) => t.includes(w)).length;
    if (s >= 3 && (!best || s > best.s)) best = { k, s };
  }
  return best?.k ?? null;
}

// ----------------------------------------------------------------------------------------------------- replies

const R = {
  greeting: {
    en: (n: string) => `Hello${n ? ` ${n}` : ''}! How can I help you today?`,
    ar: (n: string) => `أهلًا${n ? ` ${n}` : ''}! كيف أقدر أساعدك اليوم؟`,
  },
  thanks: {
    en: 'You are welcome! Anything else I can help with?',
    ar: 'العفو! تبي شي ثاني أقدر أساعدك فيه؟',
  },
  handoff: {
    asked_for_person: {
      en: 'Of course. I am passing you to a colleague now; they will reply here shortly.',
      ar: 'أكيد. بحوّلك الحين لأحد زملائي، وبيرد عليك هنا قريبًا.',
    },
    complaint: {
      en: 'I am sorry about this. I am passing you to a colleague now so they can sort it out for you.',
      ar: 'نعتذر عن هذا. بحوّلك الحين لأحد زملائي عشان يحل الموضوع معك.',
    },
    not_understood: {
      en: 'Let me get a colleague to help with that. They will reply here shortly.',
      ar: 'خلني أحوّلك لأحد زملائي يساعدك في هذا، وبيرد عليك هنا قريبًا.',
    },
    too_many_turns: {
      en: 'A colleague will take it from here and reply shortly.',
      ar: 'بيكمل معك أحد زملائي ويرد عليك قريبًا.',
    },
    orders_by_staff: {
      en: 'A colleague will confirm your order with you shortly.',
      ar: 'بيأكد معك أحد زملائي الطلب قريبًا.',
    },
  },
  help: {
    en: 'I can help with prices, stock, orders, delivery and store hours. What are you looking for?',
    ar: 'أقدر أساعدك في الأسعار، التوفر، الطلبات، التوصيل وأوقات الدوام. وش تدوّر عليه؟',
  },
  inStock: { en: 'in stock', ar: 'متوفر' },
  few: { en: (n: number) => `only ${n} left`, ar: (n: number) => `باقي ${n} فقط` },
  onSale: {
    en: (was: string) => `on sale, usually ${was}`,
    ar: (was: string) => `عليه خصم، سعره العادي ${was}`,
  },
  quotedDiffers: { en: (p: string) => `Today's price is ${p}.`, ar: (p: string) => `سعره اليوم ${p}.` },
  soldOut: {
    en: (l: string) => `${l} is sold out right now. Shall I message you as soon as it is back?`,
    ar: (l: string) => `${l} خلصان حاليًا. تبيني أراسلك أول ما يتوفر؟`,
  },
  offer: { en: 'Would you like to order? Just tell me how many.', ar: 'تبي تطلب؟ بس قول لي كم حبة.' },
  confirmAsk: {
    en: (spoken: boolean): string =>
      spoken ? 'Shall I place the order?' : 'Shall I place the order? Reply YES to confirm.',
    ar: (spoken: boolean): string => (spoken ? 'أأكد لك الطلب؟' : 'أأكد لك الطلب؟ رد بـ "نعم" للتأكيد.'),
  },
  total: { en: (t: string) => `Total: ${t}.`, ar: (t: string) => `المجموع: ${t}.` },
  confirmed: {
    en: (t: string) =>
      `Done! Your order {{order}} is placed (${t}). We will message you when it is on its way.`,
    ar: (t: string) => `تم! طلبك {{order}} مسجّل (${t}). بنراسلك أول ما يطلع للتوصيل.`,
  },
  cancelled: {
    en: 'No problem, I have not placed it. Anything else?',
    ar: 'ولا يهمك، ما سجلت الطلب. تبي شي ثاني؟',
  },
  waitlisted: {
    en: 'Done, I will message you as soon as it is back in stock.',
    ar: 'تم، بنراسلك أول ما يرجع يتوفر.',
  },
  okAnythingElse: { en: 'Sure. Anything else I can help with?', ar: 'تمام. تبي شي ثاني؟' },
  howMany: { en: 'How many would you like?', ar: 'كم حبة تبي؟' },
  onlyHave: {
    en: (n: number, l: string) => `We have only ${n} of ${l} right now.`,
    ar: (n: number, l: string) => `متوفر عندنا ${n} فقط من ${l} حاليًا.`,
  },
};

const pick = <T>(m: { en: T; ar: T }, lang: Lang): T => m[lang];

function lineText(l: BasketLine, lang: Lang, money: TurnInput['money']) {
  const each = money(l.unitPriceMinor, lang);
  const sale =
    l.regularPriceMinor && l.regularPriceMinor > l.unitPriceMinor
      ? pick(R.onSale, lang)(money(l.regularPriceMinor, lang))
      : null;
  return lang === 'ar'
    ? `${l.quantity} × ${l.label} بسعر ${each} للحبة${sale ? ` (${sale})` : ''}`
    : `${l.quantity} × ${l.label} at ${each} each${sale ? ` (${sale})` : ''}`;
}

function basketTotal(b: BasketLine[]) {
  return b.reduce((s, l) => s + l.unitPriceMinor * l.quantity, 0);
}

function deliveryNote(input: TurnInput): string | null {
  const k = input.knowledge.find((x) => x.topic === 'delivery');
  return k ? (input.lang === 'ar' && k.answerAr ? k.answerAr : k.answer) : null;
}

function confirmText(input: TurnInput, basket: BasketLine[]): string {
  const { lang, money } = input;
  const parts = [
    `${basket.map((l) => lineText(l, lang, money)).join(lang === 'ar' ? '، ' : ', ')}.`,
    pick(R.total, lang)(money(basketTotal(basket), lang)),
  ];
  const d = deliveryNote(input);
  if (d) parts.push(d);
  parts.push(pick(R.confirmAsk, lang)(!!input.spoken));
  return parts.join(lang === 'ar' ? ' ' : ' ');
}

const quantityOnly = (text: string): number | null => statedQuantity(text);

// ------------------------------------------------------------------------------------------------------- turn

export function assistantTurn(input: TurnInput): TurnResult {
  const { text, lang, money } = input;
  const state: AssistantState = { ...input.state, turns: (input.state.turns ?? 0) + 1 };
  const c = classify(text);
  const done = (r: Omit<TurnResult, 'knowledgeIds'> & { knowledgeIds?: string[] }): TurnResult => ({
    knowledgeIds: [],
    ...r,
  });
  const handoff = (reason: HandoffReason): TurnResult =>
    done({
      reply: pick(R.handoff[reason], lang),
      actions: [{ type: 'handoff', reason }],
      state: {},
      intent: `handoff:${reason}`,
    });

  if (c.human) return handoff('asked_for_person');
  if (c.complaint) return handoff('complaint');
  if ((state.turns ?? 0) > input.maxTurns) return handoff('too_many_turns');

  const found = extractOrder(text, input.catalogue);
  // A clear question about delivery, payment, hours or stores wins over a weak product match ("deliver to Al Wakra?").
  const extracted =
    c.topic &&
    c.topic !== 'general' &&
    found.lines.every((l) => l.score < 0.6) &&
    matchKnowledge(text, input.knowledge)
      ? { ...found, lines: [] }
      : found;
  const basket = state.basket ?? [];

  // 1. Answers to the assistant's own questions.
  if (state.awaiting === 'confirm' && basket.length && !extracted.lines.length) {
    const qty = quantityOnly(text);
    if (qty && basket.length === 1) {
      const b = [{ ...basket[0]!, quantity: qty }];
      return done({
        reply: confirmText(input, b),
        actions: [],
        state: { ...state, basket: b, awaiting: 'confirm' },
        intent: 'order_quantity',
      });
    }
    if (c.yes) {
      if (!input.takeOrders) return handoff('orders_by_staff');
      return done({
        reply: pick(R.confirmed, lang)(money(basketTotal(basket), lang)),
        actions: [
          {
            type: 'record_order',
            lines: basket.map((l) => ({ productId: l.productId, quantity: l.quantity })),
          },
        ],
        state: { turns: state.turns },
        intent: 'order_confirmed',
      });
    }
    if (c.no)
      return done({
        reply: pick(R.cancelled, lang),
        actions: [],
        state: { turns: state.turns },
        intent: 'order_cancelled',
      });
  }
  if (state.awaiting === 'order_offer' && basket.length && !extracted.lines.length) {
    const qty = quantityOnly(text) ?? (c.yes ? null : undefined);
    if (qty !== undefined || c.yes) {
      if (!input.takeOrders) return handoff('orders_by_staff');
      const b = basket.map((l) => ({ ...l, quantity: qty ?? l.quantity }));
      return done({
        reply: confirmText(input, b),
        actions: [],
        state: { ...state, basket: b, awaiting: 'confirm' },
        intent: 'order_started',
      });
    }
    if (c.no)
      return done({
        reply: pick(R.okAnythingElse, lang),
        actions: [],
        state: { turns: state.turns },
        intent: 'order_declined',
      });
  }
  if (state.awaiting === 'waitlist' && state.waitlistProductId && !extracted.lines.length) {
    if (c.yes)
      return done({
        reply: pick(R.waitlisted, lang),
        actions: [{ type: 'waitlist', productId: state.waitlistProductId }],
        state: { turns: state.turns },
        intent: 'waitlisted',
      });
    if (c.no)
      return done({
        reply: pick(R.okAnythingElse, lang),
        actions: [],
        state: { turns: state.turns },
        intent: 'waitlist_declined',
      });
  }

  // 2. Products named: price and stock, then an order (confirmed first) or the waitlist.
  if (extracted.lines.length) {
    const sold = extracted.lines.filter((l) => l.product.stock === 0);
    const avail = extracted.lines.filter((l) => l.product.stock !== 0);
    if (!avail.length) {
      const p = sold[0]!.product;
      return done({
        reply: pick(R.soldOut, lang)(labelFor(p, lang)),
        actions: [],
        state: { turns: state.turns, awaiting: 'waitlist', waitlistProductId: p.id },
        intent: 'sold_out',
      });
    }
    const info = avail.map((l) => {
      const p = l.product;
      const price = shelfPriceMinor(p);
      const bits = [money(price, lang)];
      if (p.salePriceMinor != null && p.salePriceMinor < p.priceMinor)
        bits.push(pick(R.onSale, lang)(money(p.priceMinor, lang)));
      bits.push(p.stock !== null && p.stock <= 5 ? pick(R.few, lang)(p.stock) : pick(R.inStock, lang));
      let s = `${labelFor(p, lang)}: ${bits.join(lang === 'ar' ? '، ' : ', ')}.`;
      if (l.quotedMinor !== null && l.quotedMinor !== price)
        s += ` ${pick(R.quotedDiffers, lang)(money(price, lang))}`;
      return s;
    });
    const soldNote = sold.map(
      (l) => pick(R.soldOut, lang)(labelFor(l.product, lang)).split(/[.؟?]/)[0] + '.',
    );
    const newBasket: BasketLine[] = avail.map((l) => ({
      productId: l.product.id,
      label: labelFor(l.product, lang),
      quantity: l.product.stock !== null ? Math.min(l.quantity, l.product.stock) : l.quantity,
      unitPriceMinor: shelfPriceMinor(l.product),
      regularPriceMinor: l.product.priceMinor,
    }));
    const stated = avail.some((l) => l.quantityStated);
    if (extracted.wantsToBuy && (stated || has(text, words(['order', 'buy', 'اطلب', 'طلب', 'اشتري'])))) {
      if (!input.takeOrders)
        return done({
          reply: [...info, pick(R.handoff.orders_by_staff, lang)].join(' '),
          actions: [{ type: 'handoff', reason: 'orders_by_staff' }],
          state: {},
          intent: 'handoff:orders_by_staff',
        });
      // The proposal says the price itself; only what needs saying on top (a wrong quoted price, short stock).
      const notes = avail.flatMap((l, i) => {
        const out: string[] = [];
        const price = shelfPriceMinor(l.product);
        if (l.quotedMinor !== null && l.quotedMinor !== price)
          out.push(pick(R.quotedDiffers, lang)(money(price, lang)));
        if (newBasket[i]!.quantity < l.quantity)
          out.push(pick(R.onlyHave, lang)(newBasket[i]!.quantity, newBasket[i]!.label));
        return out;
      });
      return done({
        reply: [...notes, ...soldNote, confirmText(input, newBasket)].join(' '),
        actions: [],
        state: { turns: state.turns, basket: newBasket, awaiting: 'confirm' },
        intent: 'order_proposed',
      });
    }
    return done({
      reply: [...info, ...soldNote, pick(R.offer, lang)].join(' '),
      actions: [],
      state: { turns: state.turns, basket: newBasket, awaiting: 'order_offer' },
      intent: 'product_info',
    });
  }

  // 3. Questions about the business.
  const k = matchKnowledge(text, input.knowledge);
  if (k)
    return done({
      reply: lang === 'ar' && k.answerAr ? k.answerAr : k.answer,
      actions: [],
      state: { ...state, misses: 0 },
      intent: `knowledge:${k.topic}`,
      knowledgeIds: [k.id],
    });
  if (c.thanks)
    return done({
      reply: pick(R.thanks, lang),
      actions: [],
      state: { ...state, misses: 0 },
      intent: 'thanks',
    });
  if (c.greeting)
    return done({
      reply: pick(R.greeting, lang)(firstName(input.customerName)),
      actions: [],
      state: { ...state, misses: 0 },
      intent: 'greeting',
    });
  if (state.awaiting === 'confirm' && basket.length)
    return done({ reply: confirmText(input, basket), actions: [], state, intent: 'confirm_again' });

  // 4. Not understood: explain once, then hand over.
  if ((state.misses ?? 0) >= 1) return handoff('not_understood');
  return done({
    reply: pick(R.help, lang),
    actions: [],
    state: { ...state, misses: (state.misses ?? 0) + 1 },
    intent: 'not_understood',
  });
}

function labelFor(p: CatalogueItem, lang: Lang) {
  return lang === 'ar' && p.nameAr ? p.nameAr : productLabelOf(p);
}

function firstName(n: string | null) {
  const f = (n ?? '').trim().split(/\s+/)[0] ?? '';
  // Handles and numbers ("+9745...", "moza.alhajri") are not names to greet.
  return /^[\p{L}][\p{L}'-]{1,30}$/u.test(f) ? f : '';
}
